<template>
	<div class="min-h-screen bg-gray-50 font-sans">
		<!-- Header -->
		<header class="bg-white border-b border-gray-200 py-4 px-6">
			<div class="w-full flex justify-between items-center flex-wrap gap-2">
				<div class="flex items-center gap-4">
					<router-link
						:to="`/theme/${themeId}/page/${pageId}`"
						class="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-transparent border border-gray-200 text-gray-500 no-underline transition-colors hover:bg-gray-50"
						title="Back to Page Edit"
					>
						<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
						</svg>
					</router-link>
					<div>
						<h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Page Object</h1>
						<p class="text-sm text-gray-500 mt-1 mb-0">Object ID: {{ objectId }}</p>
					</div>
				</div>
				<button
					type="button"
					class="inline-flex items-center gap-2 bg-orange-7 text-white py-2.5 px-4 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
					:disabled="isSaving"
					@click="saveObject"
				>
					<svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
						<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
						<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
					</svg>
					<svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
					</svg>
					{{ isSaving ? 'Saving...' : 'Save Changes' }}
				</button>
			</div>
		</header>

		<!-- Loading State -->
		<div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
			<svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
				<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
				<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
			</svg>
			<span class="ml-3 text-gray-500 text-sm">Loading page object...</span>
		</div>

		<!-- Main Content -->
		<main v-else class="p-6">
			<div class="flex flex-col gap-6">
				<div class="bg-white rounded-xl shadow-sm overflow-hidden">
				<div class="flex justify-between items-center px-6 py-4 border-b border-gray-200">
					<h2 class="text-lg font-semibold text-gray-900 m-0">Object Properties</h2>
					<span class="text-xs font-medium uppercase tracking-wide text-gray-500 bg-gray-100 rounded-full px-3 py-1">Base object</span>
				</div>
				<div class="p-6">
					<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
							<input v-model="pageObject.name" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
							<input :value="pageObject.type" type="text" readonly class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm box-border bg-gray-50 text-gray-500 cursor-not-allowed focus:outline-none">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">X</label>
							<input v-model.number="pageObject.x_axis" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Y</label>
							<input v-model.number="pageObject.y_axis" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Width</label>
							<input v-model.number="pageObject.width" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Height</label>
							<input v-model.number="pageObject.height" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Rotation</label>
							<input v-model.number="pageObject.rotate" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Z-Index</label>
							<input v-model.number="pageObject.z_axis" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Opacity</label>
							<input v-model.number="pageObject.opacity" type="number" step="0.1" min="0" max="1" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Border Width</label>
							<input v-model.number="pageObject.borderwidth" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Border Color</label>
							<div class="flex items-center gap-2">
								<input
									:value="toPickerHex(pageObject.bordercolor, '#000000')"
									type="color"
									class="w-10 h-10 p-0.5 border border-gray-300 rounded-lg cursor-pointer bg-white shrink-0"
									aria-label="Pick border color"
									@input="pageObject.bordercolor = ($event.target as HTMLInputElement).value"
								>
								<input
									v-model="pageObject.bordercolor"
									type="text"
									class="w-full px-3 py-2 border rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:border-transparent"
									:class="isColorInvalid(pageObject.bordercolor) ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-orange-5'"
									placeholder="#000000"
									@blur="pageObject.bordercolor = normalizeColor(pageObject.bordercolor)"
								>
							</div>
							<p v-if="isColorInvalid(pageObject.bordercolor)" class="text-xs text-red-500 mt-1 mb-0">Enter a valid hex color, e.g. #000000</p>
						</div>
					</div>
					<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
						<div class="p-3 border border-gray-200 rounded-lg">
							<label class="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									:checked="!!pageObject.autoinclude"
									@change="pageObject.autoinclude = ($event.target as HTMLInputElement).checked ? 1 : 0"
									class="w-4 h-4"
								>
								<span>Autoinclude</span>
							</label>
						</div>
						<div class="p-3 border border-gray-200 rounded-lg">
							<label class="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									:checked="!!pageObject.editable"
									@change="pageObject.editable = ($event.target as HTMLInputElement).checked ? 1 : 0"
									class="w-4 h-4"
								>
								<span>Editable</span>
							</label>
						</div>
						<div class="p-3 border border-gray-200 rounded-lg">
							<label class="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									:checked="!!pageObject.transformable"
									@change="pageObject.transformable = ($event.target as HTMLInputElement).checked ? 1 : 0"
									class="w-4 h-4"
								>
								<span>Transformable</span>
							</label>
						</div>
						<div class="p-3 border border-gray-200 rounded-lg">
							<label class="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									:checked="!!pageObject.decorative"
									@change="pageObject.decorative = ($event.target as HTMLInputElement).checked ? 1 : 0"
									class="w-4 h-4"
								>
								<span>Decorative</span>
							</label>
						</div>
					</div>
				</div>
				</div>

				<div v-if="subObject" class="bg-white rounded-xl shadow-sm overflow-hidden border-l-4 border-orange-5">
					<div class="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-orange-50 rounded-tr-xl">
						<h2 class="text-lg font-semibold text-gray-900 m-0">
							<i :class="pageObject.type === 'photo' ? 'fa fa-image' : 'fa fa-font'" class="mr-2 text-orange-7"></i>
							{{ pageObject.type === 'photo' ? 'Photo Properties' : 'Text Properties' }}
						</h2>
						<span class="text-xs font-medium uppercase tracking-wide text-orange-7 bg-orange-100 rounded-full px-3 py-1">{{ pageObject.type === 'photo' ? 'object_photo' : 'object_text' }}</span>
					</div>
					<div class="p-6">
						<div v-if="pageObject.type === 'photo'">
							<label class="block text-sm font-medium text-gray-700 mb-1">Image</label>
							<div class="flex items-start gap-4">
								<img
									v-if="subObject.source"
									:src="subObject.source"
									alt="Object image"
									class="w-24 h-24 rounded object-contain border border-gray-200"
								>
								<div v-else class="w-24 h-24 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-sm">No image</div>
								<div class="flex flex-col gap-2">
									<input
										ref="fileInputRef"
										type="file"
										accept="image/*,.svg"
										class="hidden"
										@change="handleFileSelect"
									>
									<button
										type="button"
										class="bg-gray-100 text-gray-700 border border-gray-300 rounded-lg px-4 py-2 text-sm cursor-pointer hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
										:disabled="isUploading"
										@click="uploadImage"
									>
										{{ isUploading ? 'Uploading...' : (subObject.source ? 'Change Image' : 'Upload Image') }}
									</button>
									<button
										v-if="subObject.source"
										type="button"
										class="bg-red-50 text-red-600 border-none rounded px-3 py-1.5 text-sm cursor-pointer hover:bg-red-100"
										@click="subObject.source = null"
									>
										Remove
									</button>
								</div>
							</div>
						</div>

						<div v-else class="flex flex-col gap-6">
							<!-- Content -->
							<div>
								<label class="block text-sm font-medium text-gray-700 mb-1">Content</label>
								<textarea
									v-model="subObject.text"
									rows="4"
									class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent resize-y"
									placeholder="Enter text content..."
								></textarea>
							</div>

							<!-- Font settings -->
							<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
								<div>
									<label class="block text-sm font-medium text-gray-700 mb-1">Font Face</label>
									<input
										v-model="subObject.fontface"
										type="text"
										class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
										placeholder="e.g. Arial"
									>
								</div>
								<div>
									<label class="block text-sm font-medium text-gray-700 mb-1">Font Size</label>
									<input
										v-model.number="subObject.fontsize"
										type="number"
										min="1"
										class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
										placeholder="e.g. 16"
									>
								</div>
								<div>
									<label class="block text-sm font-medium text-gray-700 mb-1">Font Color</label>
									<div class="flex items-center gap-2">
										<input
											:value="toPickerHex(subObject.fontcolor, '#000000')"
											type="color"
											class="w-10 h-10 p-0.5 border border-gray-300 rounded-lg cursor-pointer bg-white shrink-0"
											aria-label="Pick font color"
											@input="subObject.fontcolor = ($event.target as HTMLInputElement).value"
										>
										<input
											v-model="subObject.fontcolor"
											type="text"
											class="w-full px-3 py-2 border rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:border-transparent"
											:class="isColorInvalid(subObject.fontcolor) ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-orange-5'"
											placeholder="#000000"
											@blur="subObject.fontcolor = normalizeColor(subObject.fontcolor)"
										>
									</div>
									<p v-if="isColorInvalid(subObject.fontcolor)" class="text-xs text-red-500 mt-1 mb-0">Enter a valid hex color, e.g. #000000</p>
								</div>
								<div>
									<label class="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
									<div class="flex items-center gap-2">
										<input
											:value="toPickerHex(subObject.bgcolor, '#ffffff')"
											type="color"
											class="w-10 h-10 p-0.5 border border-gray-300 rounded-lg cursor-pointer bg-white shrink-0"
											aria-label="Pick background color"
											@input="subObject.bgcolor = ($event.target as HTMLInputElement).value"
										>
										<input
											v-model="subObject.bgcolor"
											type="text"
											class="w-full px-3 py-2 border rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:border-transparent"
											:class="isColorInvalid(subObject.bgcolor) ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-orange-5'"
											placeholder="#ffffff"
											@blur="subObject.bgcolor = normalizeColor(subObject.bgcolor)"
										>
									</div>
									<p v-if="isColorInvalid(subObject.bgcolor)" class="text-xs text-red-500 mt-1 mb-0">Enter a valid hex color, e.g. #ffffff</p>
								</div>
								<div>
									<label class="block text-sm font-medium text-gray-700 mb-1">Alignment</label>
									<select
										v-model="subObject.align"
										class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent bg-white"
									>
										<option value="Left">Left</option>
										<option value="Center">Center</option>
										<option value="Right">Right</option>
									</select>
								</div>
							</div>

							<!-- Style toggles -->
							<div class="grid grid-cols-1 md:grid-cols-3 gap-4">
								<div class="p-3 border border-gray-200 rounded-lg">
									<label class="flex items-center gap-2 cursor-pointer">
										<input
											type="checkbox"
											:checked="!!subObject.fontbold"
											@change="subObject.fontbold = ($event.target as HTMLInputElement).checked ? 1 : 0"
											class="w-4 h-4"
										>
										<span class="font-bold">Bold</span>
									</label>
								</div>
								<div class="p-3 border border-gray-200 rounded-lg">
									<label class="flex items-center gap-2 cursor-pointer">
										<input
											type="checkbox"
											:checked="!!subObject.fontitalic"
											@change="subObject.fontitalic = ($event.target as HTMLInputElement).checked ? 1 : 0"
											class="w-4 h-4"
										>
										<span class="italic">Italic</span>
									</label>
								</div>
								<div class="p-3 border border-gray-200 rounded-lg">
									<label class="flex items-center gap-2 cursor-pointer">
										<input
											type="checkbox"
											:checked="!!subObject.fontunderline"
											@change="subObject.fontunderline = ($event.target as HTMLInputElement).checked ? 1 : 0"
											class="w-4 h-4"
										>
										<span class="underline">Underline</span>
									</label>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</main>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, onBeforeRouteLeave } from 'vue-router';
import { httpClient } from 'utils/http';
import Swal from 'sweetalert2';

export default defineComponent({
	name: 'PageObjectEditPage',
	setup() {
		const route = useRoute();
		const themeId = route.params.id as string;
		const pageId = route.params.pageid as string;
		const objectId = route.params.objectid as string;

		const pageObject = ref<any>({});
		const subObject = ref<any>(null);
		const isLoading = ref(false);
		const isSaving = ref(false);
		const isUploading = ref(false);
		const fileInputRef = ref<HTMLInputElement | null>(null);

		const savedSnapshot = ref<string>('');
		const takeSnapshot = () => {
			savedSnapshot.value = JSON.stringify({ pageObject: pageObject.value, subObject: subObject.value });
		};
		const isDirty = () => JSON.stringify({ pageObject: pageObject.value, subObject: subObject.value }) !== savedSnapshot.value;

		const loadObject = async () => {
			isLoading.value = true;
			try {
				const { data } = await httpClient.get(`/api/object/${objectId}`);
				pageObject.value = data;
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load page object',
					icon: 'error',
				});
				// Snapshot even on failure so an unloaded editor is never considered dirty.
				takeSnapshot();
				isLoading.value = false;
				return;
			}
			try {
				const { data: sub } = await httpClient.get(`/api/object/${objectId}/subobject`);
				subObject.value = sub;
			} catch (error: any) {
				subObject.value = null;
				Swal.fire({
					title: 'Error',
					text: `Failed to load the ${pageObject.value.type === 'photo' ? 'photo' : 'text'} properties: ${error.message || 'unknown error'}`,
					icon: 'error',
				});
			} finally {
				takeSnapshot();
				isLoading.value = false;
			}
		};

		// Returns true when everything (object + sub-object) was saved successfully.
		const saveObject = async (): Promise<boolean> => {
			// Normalize valid values (trim, lowercase, expand #rgb) before persisting,
			// and block the save entirely if any color is still invalid.
			pageObject.value.bordercolor = normalizeColor(pageObject.value.bordercolor);
			if (subObject.value && pageObject.value.type !== 'photo') {
				subObject.value.fontcolor = normalizeColor(subObject.value.fontcolor);
				subObject.value.bgcolor = normalizeColor(subObject.value.bgcolor);
			}
			const invalidFields: string[] = [];
			if (isColorInvalid(pageObject.value.bordercolor)) invalidFields.push('Border Color');
			if (subObject.value && pageObject.value.type !== 'photo') {
				if (isColorInvalid(subObject.value.fontcolor)) invalidFields.push('Font Color');
				if (isColorInvalid(subObject.value.bgcolor)) invalidFields.push('Background Color');
			}
			if (invalidFields.length > 0) {
				Swal.fire({
					title: 'Invalid Color',
					text: `Please fix the following color field${invalidFields.length > 1 ? 's' : ''} before saving: ${invalidFields.join(', ')}. Use a hex value like #000000.`,
					icon: 'error',
				});
				return false;
			}
			isSaving.value = true;
			const subLabel = pageObject.value.type === 'photo' ? 'photo' : 'text';
			try {
				await httpClient.put(`/api/object/${objectId}`, pageObject.value);
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to save page object',
					icon: 'error',
				});
				isSaving.value = false;
				return false;
			}
			try {
				if (subObject.value) {
					if (subObject.value.id == null) {
						throw new Error(`the ${subLabel} record has no id`);
					}
					const subEndpoint = pageObject.value.type === 'photo' ? 'objectphoto' : 'objecttext';
					await httpClient.put(`/api/${subEndpoint}/${subObject.value.id}`, subObject.value);
				}
				takeSnapshot();
				Swal.fire({
					title: 'Saved',
					text: 'Page object has been saved',
					icon: 'success',
					timer: 1500,
				});
				return true;
			} catch (error: any) {
				Swal.fire({
					title: 'Partially Saved',
					text: `The object was saved, but its ${subLabel} properties failed to save: ${error.message || 'unknown error'}`,
					icon: 'warning',
				});
				return false;
			} finally {
				isSaving.value = false;
			}
		};

		// Native color inputs only accept #rrggbb; normalize other values so the swatch stays usable.
		const toPickerHex = (value: any, fallback: string): string => {
			if (typeof value === 'string') {
				const v = value.trim();
				if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
				if (/^#[0-9a-fA-F]{3}$/.test(v)) {
					return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`.toLowerCase();
				}
			}
			return fallback;
		};

		// Empty/unset values are allowed; only non-empty values must be a valid hex color.
		const isColorInvalid = (value: any): boolean => {
			if (value == null) return false;
			if (typeof value !== 'string') return true;
			const v = value.trim();
			if (v === '') return false;
			return !/^#[0-9a-fA-F]{3}$/.test(v) && !/^#[0-9a-fA-F]{6}$/.test(v);
		};

		// Normalize on blur: trim, lowercase, expand #rgb to #rrggbb. Invalid values are left as-is
		// so the inline error stays visible and the user can correct them.
		const normalizeColor = (value: any): any => {
			if (typeof value !== 'string') return value;
			const v = value.trim();
			if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
			if (/^#[0-9a-fA-F]{3}$/.test(v)) {
				return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`.toLowerCase();
			}
			return v === '' ? v : value;
		};

		const uploadImage = () => {
			fileInputRef.value?.click();
		};

		const handleFileSelect = async (event: Event) => {
			const input = event.target as HTMLInputElement;
			const file = input.files?.[0];
			// Reset so selecting the same file again re-triggers the change event.
			input.value = '';
			if (!file) return;

			isUploading.value = true;
			try {
				const formData = new FormData();
				formData.append('file', file);

				const response = await httpClient.post('/api/upload', formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				});

				if (response.data?.url) {
					subObject.value.source = response.data.url;
				} else if (typeof response.data === 'string') {
					subObject.value.source = response.data;
				} else {
					Swal.fire({
						title: 'Error',
						text: 'Upload succeeded but no URL returned',
						icon: 'error',
					});
				}
			} catch (error: any) {
				Swal.fire({
					title: 'Upload Failed',
					text: error.response?.data?.message || error.message || 'Failed to upload file',
					icon: 'error',
				});
			} finally {
				isUploading.value = false;
			}
		};

		onBeforeRouteLeave(async () => {
			if (!isDirty()) return true;
			const result = await Swal.fire({
				title: 'Unsaved Changes',
				text: 'You have unsaved changes on this object. Do you want to save them before leaving?',
				icon: 'warning',
				showDenyButton: true,
				showCancelButton: true,
				confirmButtonText: 'Save & Leave',
				denyButtonText: 'Discard Changes',
				cancelButtonText: 'Stay Here',
			});
			if (result.isConfirmed) {
				// Only leave when everything saved; the save flow already reported the error.
				return await saveObject();
			}
			if (result.isDenied) return true;
			return false;
		});

		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (isDirty()) {
				event.preventDefault();
				event.returnValue = '';
			}
		};

		onMounted(() => {
			window.addEventListener('beforeunload', handleBeforeUnload);
			loadObject();
		});

		onBeforeUnmount(() => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		});

		return {
			themeId,
			pageId,
			objectId,
			pageObject,
			subObject,
			isLoading,
			isSaving,
			saveObject,
			uploadImage,
			handleFileSelect,
			isUploading,
			fileInputRef,
			toPickerHex,
			isColorInvalid,
			normalizeColor,
		};
	},
});
</script>
